import React, { useMemo, useCallback } from "react";
import { Select } from "components/Select/Select";
import { InputTitle } from "./InputTitle";
import type {
  OrderCodePartOptionStrict,
  OrderCodePartStrict,
} from "stores/product";
import type { OnChange } from "../OrderCodePartOption";
import { useIsLoadingEffect } from "./hooks/useIsLoadingEffect";

type SelectInputProps = {
  option: OrderCodePartOptionStrict;
  part: OrderCodePartStrict;
  onChange: OnChange;
  disabled?: boolean;
};

export const SelectInput = ({
  option,
  part,
  onChange,
  disabled,
}: SelectInputProps) => {
  const [isLoading, setIsLoading] = useIsLoadingEffect();
  const options = useMemo(() => {
    return option.signatures
      ?.filter((sig) => sig.state <= 2)
      .map((signature) => {
        const suffix =
          signature?.value !== signature?.title
            ? ` (${signature?.presentation})`
            : "";
        return {
          value: signature?.key,
          label: `${signature?.title} ${suffix}`,
          disabled: signature?.state !== 0,
        };
      });
  }, [option]);

  const selectedOption = useMemo(
    () => options?.find((opt) => opt.value === option.currentSignature?.key),
    [options, option.currentSignature],
  );

  const handleOnChange = useCallback(
    (selected: { value: string; label: string } | null) => {
      if (!selected) {
        return;
      }
      onChange(selected.value);
      setIsLoading(true);
    },
    [onChange, setIsLoading],
  );

  return (
    <>
      <InputTitle title={option.title} isloading={isLoading} />
      <Select
        name={`config-${part.key}-${option.key}`}
        data-recording-disable={true}
        options={options}
        onChange={handleOnChange}
        isDisabled={disabled}
        value={selectedOption}
        testId={`select-${part.key}-${option.key}`}
        // Todo: Implement onMenuOpen action
        onMenuOpen={() => {}}
        menuShouldScrollIntoView={false}
        // Todo: Implement menuPlacement logic based on view (modal vs non-modal)
        // menuPlacement={isModalView ? "bottom" : "auto"}
        menuPlacement="auto"
        menuPosition="fixed"
      />
    </>
  );
};
